"use client";
import PropTypes from "prop-types";
import { useCallback, useRef } from "react";
import { getBaseUrl } from "../../../utils/common";
/**
 * Returns Video Player
 * @returns
 */
const VideoPlayer = ({
  videoUrl,
  width = "100%",
  height = "100%",
  title,
  classes,
  isControls = false,
  isAutoPlay = false,
  isMuted = false,
  isLoop = false,
  isPlaysInline = false,
  isAllowDownload = false,
  onPlaying = null,
  customRef = null,
  testId = "",
  preload = "metadata",
  poster = "",
  id = "",
  onMouseOver = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onEnded = () => {},
  onError = () => {},
  ...restParams
}) => {
  const videoRef = useRef(null);
  const posterUrl = getBaseUrl(poster);
  /**
   * Play Pause handler.
   * @param {bool} isPlaying
   */
  const onPlayPauseHandler = (e, isPlaying) => {
    onPlaying?.(e, isPlaying);
  };

  /**
   *
   * @param {*} ref
   */
  const getRef = (element) => {
    videoRef.current = element;
    if (typeof customRef === "function") {
      customRef(element);
    } else if (customRef) {
      customRef.current = element;
    }
  };

  const formattedVideoUrl = videoUrl?.includes("#t=") ? videoUrl : `${videoUrl}#t=0.01`;
  const shouldDeferVideoSource = preload === "none" && !isAutoPlay;

  const renderVideoPlayer = useCallback(() => {
    return (
      <video
        ref={getRef}
        src={shouldDeferVideoSource ? undefined : formattedVideoUrl}
        data-src={formattedVideoUrl}
        width={width}
        height={height}
        title={title}
        className={classes}
        controlsList={isAllowDownload ? "" : "nodownload"}
        preload={preload}
        controls={isControls}
        autoPlay={isAutoPlay}
        muted={isMuted}
        loop={isLoop}
        data-testid={testId}
        playsInline={isPlaysInline}
        webkit-playsinline={isPlaysInline ? "true" : undefined}
        x5-playsinline={isPlaysInline ? "true" : undefined}
        x-webkit-airplay="allow"
        onPause={(e) => onPlayPauseHandler(e, false)}
        onPlay={(e) => onPlayPauseHandler(e, true)}
        poster={posterUrl}
        id={id}
        onMouseOver={onMouseOver}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onEnded={onEnded}
        onError={onError}
        {...restParams}
      />
    );
  }, [
    formattedVideoUrl,
    isMuted,
    isAutoPlay,
    classes,
    height,
    id,
    isAllowDownload,
    isControls,
    isLoop,
    isPlaysInline,
    onEnded,
    onError,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onPlayPauseHandler,
    posterUrl,
    preload,
    shouldDeferVideoSource,
    testId,
    title,
    width,
    restParams,
  ]);

  return renderVideoPlayer();
};

VideoPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  title: PropTypes.string,
  classes: PropTypes.string,
  isControls: PropTypes.bool,
  isAutoPlay: PropTypes.bool,
  isMuted: PropTypes.bool,
  isLoop: PropTypes.bool,
  isPlaysInline: PropTypes.bool,
  isAllowDownload: PropTypes.bool,
  onPlaying: PropTypes.func,
  customRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  testId: PropTypes.string,
  preload: PropTypes.string,
  poster: PropTypes.string,
  id: PropTypes.string,
  onMouseOver: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onEnded: PropTypes.func,
  onError: PropTypes.func,
};

export default VideoPlayer;
